import { z } from 'zod';

// Fixed, closed variable catalogue (Person / Kontakt / Firma). The template
// stores only which tokens it uses; values resolve at IMM-258.2 time. An off-list
// token is rejected on save.
export const SIGNATURE_VARIABLES = [
  'Vorname',
  'Nachname',
  'Position',
  'Profilfoto', // Person
  'Telefon',
  'Mobil',
  'E-Mail', // Kontakt
  'Firmenname',
  'Website',
  'Adresse',
  'Logo', // Firma
] as const;

export type SignatureVariable = (typeof SIGNATURE_VARIABLES)[number];

// The image-typed variables: their resolved value is a presigned image URL, so
// they must render as an `<img>`, never as text. Image-ness used to be implicit in
// three unconnected places (the resolver assigning a URL, the renderer's tagName
// check, the separate data-sig-logo path); this makes it one shared fact both the
// backend renderer and the web-app editor import. `SIGNATURE_VARIABLES` membership
// is unchanged — this is a labelled subset of it.
export const IMAGE_SIGNATURE_VARIABLES = ['Profilfoto', 'Logo'] as const satisfies readonly SignatureVariable[];

export type ImageSignatureVariable = (typeof IMAGE_SIGNATURE_VARIABLES)[number];

export function isImageSignatureVariable(value: string): value is ImageSignatureVariable {
  return (IMAGE_SIGNATURE_VARIABLES as readonly string[]).includes(value);
}

// UPPER_CASE union per the domain-constant convention; German labels
// ("Klein · 120 px", …) + px map live in @immo/i18n / a domain constant.
export const SignatureLogoSizeSchema = z.enum(['SMALL', 'MEDIUM', 'LARGE']);

export type SignatureLogoSize = z.infer<typeof SignatureLogoSizeSchema>;

// Markup ceiling — visible-text ≤ 5.000 is a domain rule, not a byte cap.
const SIGNATURE_TEMPLATE_MAX_BYTES = 200_000;

export const CompanySignatureResponseSchema = z.object({
  workspaceId: z.uuid(),
  template: z.string(), // sanitised HTML; "" when unset
  logoSize: SignatureLogoSizeSchema, // default 'MEDIUM'
  updatedAt: z.iso.datetime().nullable(),
});

export type CompanySignatureResponse = z.infer<typeof CompanySignatureResponseSchema>;

export const UpdateCompanySignatureBodySchema = z
  .object({
    template: z.string().max(SIGNATURE_TEMPLATE_MAX_BYTES), // may be "" (clear)
    logoSize: SignatureLogoSizeSchema.default('MEDIUM'),
  })
  .strict();

export type UpdateCompanySignatureBody = z.infer<typeof UpdateCompanySignatureBodySchema>;

export const CompanySignaturePathParamsSchema = z.object({ workspaceId: z.uuid() });

// Resolved, mail-safe signature HTML, or null when the workspace has no signature
// set / the template is blank (graceful degradation -- the composer treats null as
// "insert nothing"). Added in IMM-258.2.
export const ResolvedCompanySignatureResponseSchema = z.object({
  html: z.string().nullable(),
});

export type ResolvedCompanySignatureResponse = z.infer<typeof ResolvedCompanySignatureResponseSchema>;

// The member whose Person/Kontakt values fill the template. Optional -- defaults to
// the calling member (the "Profil" switcher passes a chosen member).
export const ResolveCompanySignatureQueryParamsSchema = z.object({ memberId: z.uuid().optional() });

export type ResolveCompanySignatureQueryParams = z.infer<typeof ResolveCompanySignatureQueryParamsSchema>;

export const COMPANY_SIGNATURE_CLASS = 'immo-signature';

export function hasCompanySignature(html: string | null | undefined): html is string {
  return html !== undefined && html !== null && html.trim() !== '';
}

export function companySignatureBlock(html: string): string {
  return `<div class="${COMPANY_SIGNATURE_CLASS}">${html}</div>`;
}
